import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@/lib/utils'

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      className,
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:shadow-sm',
      className,
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

/* ── Minimal tab group — shared styles for bordered pill toggles ─────── */

const MINIMAL_GROUP_CLASSES =
  'inline-flex h-auto items-center rounded-md border border-border text-muted-foreground'

// `h-7` matches the height of the Save and Run buttons in the backtest
// header (both h-7 too) so the editor/chart toggle sits flush in the same
// horizontal row. `py-0` because the explicit height now controls vertical
// sizing — `py-1` on top of `h-7` would clip the text.
//
// Only the ACTIVE tab is filled (see MINIMAL_ITEM_ACTIVE). Inactive tabs are
// transparent so they don't read as "highlighted"; hover only brightens the
// text for affordance (no fill), keeping the segmented control's structure
// from the group border alone.
const MINIMAL_ITEM_CLASSES =
  'inline-flex h-7 items-center justify-center gap-1 whitespace-nowrap px-2.5 py-0 text-xs font-medium transition-colors first:rounded-l-[calc(theme(borderRadius.md)-1px)] last:rounded-r-[calc(theme(borderRadius.md)-1px)] bg-transparent text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50'

// Active state: `--primary` (purple-blue) fill softened to 80% so it reads
// as "selected" without dominating. `text-white` (not `text-primary-foreground`)
// because in this theme `--primary-foreground` is set equal to `--background`
// so it can act as negative-space text — that's near-black on the dark
// sidebar, which would be unreadable on a bright primary fill.
const MINIMAL_ITEM_ACTIVE = 'bg-primary/80 text-white'

/** Radix Tabs variant — use inside a <Tabs> context. */
const MinimalTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn(MINIMAL_GROUP_CLASSES, className)} {...props} />
))
MinimalTabsList.displayName = 'MinimalTabsList'

const MinimalTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      MINIMAL_ITEM_CLASSES,
      // Strong active state (matches MINIMAL_ITEM_ACTIVE): a primary fill so the
      // selected tab clearly stands out from the muted, near-transparent
      // inactive/hover tabs. text-white because --primary-foreground equals
      // --background in this theme (near-black — unreadable on the bright fill).
      'data-[state=active]:bg-primary/80 data-[state=active]:text-white',
      className,
    )}
    {...props}
  />
))
MinimalTabsTrigger.displayName = 'MinimalTabsTrigger'

/* ── Underline tab group — full-width baseline, active tab underlined ─────
   Radix version of `HorizontalTab` (components/panel/horizontal-tab.tsx), the
   ticker-stats / key-stats look. The list carries a full-width bottom border so
   the row reads as a labelled baseline instead of a floating pill; each trigger
   draws a 2px underline that becomes foreground-colored when active. */

const UnderlineTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    // border-b-strong (not plain border) so this baseline reads as the primary
    // chrome→content divider and outranks any lighter toolbar border below it.
    // Triggers begin at their label (no left pad) and carry right padding for
    // width; gap-3 adds the space between tabs (see UnderlineTabsTrigger).
    className={cn('flex h-auto w-full items-center gap-3 border-b border-border-strong', className)}
    {...props}
  />
))
UnderlineTabsList.displayName = 'UnderlineTabsList'

const UnderlineTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Symmetric px-3 so the label is centered within its underline; the strip
      // inset (list pl-4) lands the underline's left edge at the title/dropdown
      // margin. Spacing between tabs comes from the list gap.
      '-mb-px inline-flex select-none items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-xs font-medium tracking-wide text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
      // Active tab picks up the brand primary (matches the Apply button), so the
      // current section reads as "selected" the same way the primary action does.
      'data-[state=active]:border-primary data-[state=active]:text-primary',
      className,
    )}
    {...props}
  />
))
UnderlineTabsTrigger.displayName = 'UnderlineTabsTrigger'

/** Plain HTML variant — for multi-toggle or non-Radix contexts. */
const MinimalTabGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(MINIMAL_GROUP_CLASSES, className)} {...props} />
  ),
)
MinimalTabGroup.displayName = 'MinimalTabGroup'

interface MinimalTabButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
}

const MinimalTabButton = React.forwardRef<HTMLButtonElement, MinimalTabButtonProps>(
  ({ className, active, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(MINIMAL_ITEM_CLASSES, active && MINIMAL_ITEM_ACTIVE, className)}
      {...props}
    />
  ),
)
MinimalTabButton.displayName = 'MinimalTabButton'

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  MinimalTabsList,
  MinimalTabsTrigger,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  MinimalTabGroup,
  MinimalTabButton,
}
